import React from "react";
import { useScrollAnimation } from "./useScrollAnimation";
import "./TechProfile.css";

/* ── data ──────────────────────────────────────────────── */
const competencies = [
  {
    emoji: "🧠",
    title: "Software Engineering",
    color: "#4da8da",
    points: [
      "Modulare Anwendungen mit Python, JavaScript & PHP",
      "REST-API-Integration & JSON-basierte Schnittstellen",
      "Datenverarbeitung: Parsing, Mapping, Aggregation",
      "Performance via Caching, Parallelisierung & effizienten Datenstrukturen",
    ],
  },
  {
    emoji: "🌐",
    title: "Web & Frontend",
    color: "#6ec6f0",
    points: [
      "Dynamische UIs mit React & Bootstrap (Dark Mode)",
      "Echtzeit-Dashboards mit WebSocket / MQTT",
      "Responsive, komponentenbasierte Architektur",
      "Zeitreihendaten-Visualisierung (Kamera-Zählwerte)",
    ],
  },
  {
    emoji: "🔄",
    title: "Automation & Scraping",
    color: "#89cff0",
    points: [
      "Browser-Automatisierung mit Selenium & Playwright",
      "Web-Scraping mit BeautifulSoup",
      "Tree-Crawling-Systeme zur rekursiven Datenerfassung",
      "Dynamisches Request-Handling (Sessions, Cookies, Tokens)",
    ],
  },
  {
    emoji: "📡",
    title: "IoT & Realtime Systems",
    color: "#4da8da",
    points: [
      "Embedded-Entwicklung mit RP2040 Microcontroller",
      "Echtzeit-Kommunikation via MQTT",
      "LED-Matrix-Steuerung (WLED, sACN, JSON-Protokolle)",
      "Sensordatenverarbeitung & Visualisierung",
    ],
  },
  {
    emoji: "🗄️",
    title: "Backend & Data",
    color: "#6ec6f0",
    points: [
      "Datenbankdesign & -integration mit MySQL",
      "PDF → strukturierte Daten → Excel Pipelines",
      "HTML → JSON → API Transformations",
      "Automatisierte Workflows & Datenpipelines",
    ],
  },
  {
    emoji: "📱",
    title: "Mobile & Cloud",
    color: "#89cff0",
    points: [
      "Mobile Apps mit Flutter",
      "Firebase: Authentication & Cloud Messaging",
      "Push-basierte Systeme & Echtzeit-Login-Freigaben",
      "Cloud-Integration & Deployment",
    ],
  },
];

const projects = [
  {
    title: "Realtime Monitoring System",
    emoji: "📊",
    color: "#4da8da",
    desc: "MQTT-Daten aus Bosch-Kameras werden live verarbeitet und in einem React-Dashboard visualisiert — IN / OUT / Live Count / Duration in Echtzeit.",
    tags: ["MQTT", "React", "WebSocket", "Bosch API"],
  },
  {
    title: "AI-powered Overlay Tool",
    emoji: "🤖",
    color: "#6ec6f0",
    desc: "Screenshot-Erfassung kombiniert mit GPT-Auswertung. System-Tray-Integration mit Echtzeit-Feedback direkt auf dem Desktop.",
    tags: ["Python", "GPT", "System Tray", "OpenAI API"],
  },
  {
    title: "Automated Data Extraction Pipeline",
    emoji: "⚙️",
    color: "#89cff0",
    desc: "PDF-Parsing strukturierter Prüfungsdaten mit automatisiertem Export in formatierte Excel-Dateien inklusive Bewertungssystem.",
    tags: ["Python", "PDF Parsing", "Excel", "Automation"],
  },
  {
    title: "Custom IoT LED Matrix Controller",
    emoji: "💡",
    color: "#4da8da",
    desc: "64×32 WS2812 LED-Matrix mit WLED-Ansteuerung. Eigene Python-Implementierung zur Pixel-Konvertierung & Streaming über das Netzwerk.",
    tags: ["Python", "WLED", "sACN", "RP2040", "IoT"],
  },
  {
    title: "High-Performance Web Scraper",
    emoji: "🕷️",
    color: "#6ec6f0",
    desc: "Parallelisiertes Crawling von Uni-Systemen mit intelligenter Cache-Optimierung und robuster Fehlerbehandlung für maximale Datenabdeckung.",
    tags: ["Python", "Selenium", "BeautifulSoup", "Threading"],
  },
];

const pillars = [
  { emoji: "⚡", label: "Performance & Skalierbarkeit" },
  { emoji: "🔗", label: "Software + Hardware Integration" },
  { emoji: "🎯", label: "Prototyp → Optimierung" },
];

/* ── sub-components ─────────────────────────────────────── */
function CompCard({ item, delay }) {
  const [ref, visible] = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`tp-comp-card ${visible ? "tp-visible" : "tp-hidden"}`}
      style={{
        transitionDelay: visible ? `${delay}ms` : "0ms",
        "--accent": item.color,
      }}
    >
      <div className="tp-comp-emoji">{item.emoji}</div>
      <h3 className="tp-comp-title">{item.title}</h3>
      <ul className="tp-comp-points">
        {item.points.map((p, i) => (
          <li key={i}>{p}</li>
        ))}
      </ul>
    </div>
  );
}

function ProjectCard({ item, delay }) {
  const [ref, visible] = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`tp-proj-card ${visible ? "tp-visible" : "tp-hidden"}`}
      style={{
        transitionDelay: visible ? `${delay}ms` : "0ms",
        "--accent": item.color,
      }}
    >
      <div className="tp-proj-top">
        <span className="tp-proj-emoji">{item.emoji}</span>
        <div className="tp-proj-title-row">
          <h3 className="tp-proj-title">{item.title}</h3>
          <span className="tp-wip-badge">🚧 In Bearbeitung</span>
        </div>
      </div>
      <p className="tp-proj-desc">{item.desc}</p>
      <div className="tp-proj-tags">
        {item.tags.map((tag, i) => (
          <span key={i} className="tp-tag">{tag}</span>
        ))}
      </div>
    </div>
  );
}

/* ── main component ─────────────────────────────────────── */
const TechProfile = () => {
  const [heroRef, heroVisible] = useScrollAnimation(0.05);

  return (
    <div className="tp-page">

      {/* ── HERO ── */}
      <section
        ref={heroRef}
        className={`tp-hero ${heroVisible ? "tp-visible" : "tp-hidden"}`}
      >
        <div className="tp-hero-badge">Technical Profile</div>
        <h1 className="tp-hero-title">
          Systeme, die <span className="tp-highlight">funktionieren.</span>
        </h1>
        <p className="tp-hero-sub">
          Leistungsfähige Software- & Hardwarelösungen mit Fokus auf
          Automatisierung, Echtzeit-Datenverarbeitung und Systemintegration —
          vom Prototyp bis zum produktiven System.
        </p>
        <div className="tp-hero-line" />
      </section>

      {/* ── COMPETENCIES ── */}
      <section className="tp-section">
        <div className="tp-section-label">Core Competencies</div>
        <div className="tp-comp-grid">
          {competencies.map((item, i) => (
            <CompCard key={i} item={item} delay={i * 80} />
          ))}
        </div>
      </section>

      {/* ── PROJECTS ── */}
      <section className="tp-section">
        <div className="tp-section-label">Selected Work</div>
        <div className="tp-wip-notice">
          <span className="tp-wip-dot" />
          Diese Projekte befinden sich aktuell in Bearbeitung — Details folgen in Kürze.
        </div>
        <div className="tp-proj-grid">
          {projects.map((item, i) => (
            <ProjectCard key={i} item={item} delay={i * 90} />
          ))}
        </div>
      </section>

      {/* ── PILLARS ── */}
      <section className="tp-pillars-section">
        <div className="tp-section-label">Engineering Approach</div>
        <div className="tp-pillars">
          {pillars.map((p, i) => (
            <div key={i} className="tp-pillar">
              <span className="tp-pillar-emoji">{p.emoji}</span>
              <span className="tp-pillar-label">{p.label}</span>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
};

export default TechProfile;
